import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getReadRelays } from './useNostrPublish';
import { useNostrQuery } from './useNostrQuery';
import { normalizeBCHAddress } from './useBCHAddress';

// ── Community Tip Wallet ──────────────────────────────────────────
//
// A dedicated BCH address users voluntarily set for community tipping.
// Stored as a kind 30078 event with d-tag "bchnostr/community-wallet".
//
// Content format (JSON):
//   { "address": "bitcoincash:q...", "split": 60 }
//
// - address: The community wallet BCH address
// - split:   Percentage of tips going to community wallet (0-100)
//            Remainder goes to the user's personal BCH address.
//            Default: 100 (all tips to community wallet)
//
// Legacy format (plain address string) is still supported for
// backwards compatibility — treated as split=100.

const COMMUNITY_WALLET_D_TAG = 'bchnostr/community-wallet';

// ── Types ─────────────────────────────────────────────────────────
export interface CommunityWalletInfo {
  address: string | null;
  /** Percentage of tips going to community wallet (0-100). Default 100. */
  split: number;
}

// ── Cache ─────────────────────────────────────────────────────────
interface CacheEntry {
  info: CommunityWalletInfo;
  fetchedAt: number;
}
const walletCache = new Map<string, CacheEntry>();
const NULL_CACHE_TTL = 5 * 60 * 1000;

const EMPTY_INFO: CommunityWalletInfo = { address: null, split: 100 };

export function invalidateCommunityWalletCache(pubkey: string) {
  walletCache.delete(pubkey);
}

export function getCachedCommunityWallet(pubkey: string): string | null {
  return walletCache.get(pubkey)?.info.address ?? null;
}

export function getCachedCommunityWalletInfo(pubkey: string): CommunityWalletInfo {
  return walletCache.get(pubkey)?.info ?? EMPTY_INFO;
}

export function hasCommunityWalletCached(pubkey: string): boolean {
  const entry = walletCache.get(pubkey);
  if (!entry) return false;
  if (entry.info.address) return true;
  return (Date.now() - entry.fetchedAt) < NULL_CACHE_TTL;
}

/**
 * Parse event content into CommunityWalletInfo.
 * Supports both JSON format and legacy plain-address format.
 */
function parseWalletContent(content: string): CommunityWalletInfo {
  const trimmed = content.trim();
  if (!trimmed) return EMPTY_INFO;

  // Try JSON format first: { "address": "...", "split": 60 }
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      const addr = normalizeBCHAddress(parsed.address || '');
      const split = typeof parsed.split === 'number'
        ? Math.max(0, Math.min(100, Math.round(parsed.split)))
        : 100;
      return { address: addr, split };
    } catch {}
  }

  // Legacy format: plain BCH address string
  const addr = normalizeBCHAddress(trimmed);
  return { address: addr, split: 100 };
}

// In-flight fetches
const _fetchInFlight = new Map<string, Promise<CommunityWalletInfo>>();

/** Prefetch community wallet info into cache */
export function prefetchCommunityWallet(pubkey: string): Promise<string | null> {
  return prefetchCommunityWalletInfo(pubkey).then(info => info.address);
}

export function prefetchCommunityWalletInfo(pubkey: string): Promise<CommunityWalletInfo> {
  if (hasCommunityWalletCached(pubkey)) return Promise.resolve(getCachedCommunityWalletInfo(pubkey));
  if (_fetchInFlight.has(pubkey)) return _fetchInFlight.get(pubkey)!;

  const promise = new Promise<CommunityWalletInfo>((resolve) => {
    const baseRelays = getReadRelays();
    const BCH_RELAY = 'wss://relay.bchnostr.com';
    const relays = baseRelays.includes(BCH_RELAY) ? baseRelays : [BCH_RELAY, ...baseRelays];

    let bestEvent: any = null;
    let responded = 0;
    let finalized = false;
    const websockets: WebSocket[] = [];

    const finalize = () => {
      if (finalized) return;
      finalized = true;

      const info = bestEvent?.content
        ? parseWalletContent(bestEvent.content)
        : EMPTY_INFO;

      walletCache.set(pubkey, { info, fetchedAt: Date.now() });
      _fetchInFlight.delete(pubkey);
      websockets.forEach(ws => { try { ws.close(); } catch {} });
      resolve(info);
    };

    const maybeFinalize = () => {
      if (bestEvent && !finalized) { finalize(); return; }
      if (responded >= relays.length) finalize();
    };

    relays.forEach(url => {
      try {
        const ws = new WebSocket(url);
        websockets.push(ws);
        const subId = 'cw_' + Math.random().toString(36).slice(2, 8);

        ws.onopen = () => {
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [30078],
            authors: [pubkey],
            '#d': [COMMUNITY_WALLET_D_TAG],
            limit: 1,
          }]));
        };

        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[1] === subId) {
              const ev = data[2];
              if (!bestEvent || ev.created_at > bestEvent.created_at) {
                bestEvent = ev;
              }
            }
            if (data[0] === 'EOSE' && data[1] === subId) {
              responded++;
              maybeFinalize();
            }
          } catch {}
        };

        ws.onerror = () => { responded++; maybeFinalize(); };
        setTimeout(() => { try { ws.close(); } catch {} }, 8000);
      } catch {
        responded++;
        maybeFinalize();
      }
    });

    setTimeout(finalize, 9000);
  });

  _fetchInFlight.set(pubkey, promise);
  return promise;
}

/** React hook to get a user's community wallet info (address + split) */
export function useCommunityWallet(pubkey: string | undefined): {
  address: string | null;
  split: number;
  loading: boolean;
} {
  const [info, setInfo] = useState<CommunityWalletInfo>(
    pubkey ? getCachedCommunityWalletInfo(pubkey) : EMPTY_INFO
  );
  const [loading, setLoading] = useState(pubkey ? !hasCommunityWalletCached(pubkey) : false);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pubkey) { setLoading(false); return; }

    if (hasCommunityWalletCached(pubkey)) {
      setInfo(getCachedCommunityWalletInfo(pubkey));
      setLoading(false);
      return;
    }

    if (fetchedRef.current === pubkey) return;
    fetchedRef.current = pubkey;
    setLoading(true);

    prefetchCommunityWalletInfo(pubkey).then(result => {
      setInfo(result);
      setLoading(false);
    });
  }, [pubkey]);

  return { address: info.address, split: info.split, loading };
}

// ── Nostr-Native Circulation Stats ───────────────────────────────
//
// Tracks BCH circulation entirely through Nostr tip receipt events
// (kind 9735). No blockchain API calls needed — everything is derived
// from the social tipping activity recorded on Nostr relays.
//
// - Tips RECEIVED = kind 9735 events where #p = this pubkey
// - Tips SENT     = kind 9735 events where author = this pubkey
// - Circulation % = (sats sent / sats received) × 100
//
// This is accurate, fast, free, and doesn't depend on any third-party
// blockchain explorer API (which were unreliable and often returned
// wrong data or units).

const TIP_RECEIPT_KIND = 9735;

export interface CirculationStats {
  /** Total sats received via tips (from kind 9735 #p tags) */
  totalReceived: number;
  /** Total sats sent as tips (from kind 9735 authored events) */
  totalSent: number;
  /** Total unique tip events (sent + received) */
  txCount: number;
  /** Number of unique people who tipped this user */
  uniqueSupporters: number;
  /** Number of unique people this user tipped */
  uniqueSupported: number;
  /** Circulation score: % of received sats that were re-tipped */
  circulationScore: number;
  loading: boolean;
  error: string | null;
}

/** Extract sats from a tip receipt event */
function extractSats(ev: any): number {
  // Try the `amount` tag first (most reliable — always present)
  const amountTag = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
  if (amountTag) {
    const n = parseInt(amountTag, 10);
    if (n > 0) return n;
  }
  // Fall back to JSON content
  try {
    const content = JSON.parse(ev.content || '{}');
    if (content.amount_sats > 0) return content.amount_sats;
  } catch {}
  return 0;
}

/**
 * Nostr-native circulation stats hook.
 * Queries kind 9735 tip receipts to compute how much BCH a user
 * has received and re-tipped within the community.
 */
export function useCirculationStats(pubkey: string | null): CirculationStats {
  const since = useMemo(() => Math.floor(Date.now() / 1000) - 365 * 86400, []);

  // Tips RECEIVED by this pubkey (others tipped them)
  const { events: tipsReceived, loading: loadingReceived } = useNostrQuery({
    filters: pubkey ? [{ kinds: [TIP_RECEIPT_KIND], '#p': [pubkey], '#t': ['bch-tip'], limit: 500, since }] : [],
    allRelays: true,
  });

  // Tips SENT by this pubkey (they tipped others)
  const { events: tipsSent, loading: loadingSent } = useNostrQuery({
    filters: pubkey ? [{ kinds: [TIP_RECEIPT_KIND], authors: [pubkey], '#t': ['bch-tip'], limit: 500, since }] : [],
    allRelays: true,
  });

  const stats = useMemo<CirculationStats>(() => {
    const loading = loadingReceived || loadingSent;

    if (!pubkey) {
      return { totalReceived: 0, totalSent: 0, txCount: 0, uniqueSupporters: 0, uniqueSupported: 0, circulationScore: 0, loading: false, error: null };
    }

    if (loading && tipsReceived.length === 0 && tipsSent.length === 0) {
      return { totalReceived: 0, totalSent: 0, txCount: 0, uniqueSupporters: 0, uniqueSupported: 0, circulationScore: 0, loading: true, error: null };
    }

    // Sum sats received (excluding self-tips)
    let totalReceived = 0;
    const supporters = new Set<string>();
    for (const ev of tipsReceived) {
      if (ev.pubkey === pubkey) continue; // Skip self-tips
      const sats = extractSats(ev);
      totalReceived += sats;
      supporters.add(ev.pubkey);
    }

    // Sum sats sent (excluding self-tips)
    let totalSent = 0;
    const supported = new Set<string>();
    for (const ev of tipsSent) {
      const recipient = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (recipient === pubkey) continue; // Skip self-tips
      const sats = extractSats(ev);
      totalSent += sats;
      if (recipient) supported.add(recipient);
    }

    // Deduplicate: count unique tip events
    const allIds = new Set<string>();
    for (const ev of tipsReceived) allIds.add(ev.id);
    for (const ev of tipsSent) allIds.add(ev.id);

    const circulationScore = totalReceived > 0
      ? Math.min(100, Math.round((totalSent / totalReceived) * 100))
      : (totalSent > 0 ? 100 : 0); // If they only send and never receive, that's 100% circulation

    return {
      totalReceived,
      totalSent,
      txCount: allIds.size,
      uniqueSupporters: supporters.size,
      uniqueSupported: supported.size,
      circulationScore,
      loading,
      error: null,
    };
  }, [pubkey, tipsReceived, tipsSent, loadingReceived, loadingSent]);

  return stats;
}

